import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Draggable from 'react-draggable';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { GetPlacementFinancials } from 'services/PlacementsService';
import {
  PREVIEW_TABLE_CONTENT,
  PREVIEW_TABLE_LABEL,
  PREVIEW_TABLE_STYLE,
  PREVIEW_TITLE_STYLE,
} from 'util/Definitions/Constants/Previews';
import CloseIcon from '@mui/icons-material/Close';
import ControlCameraIcon from '@mui/icons-material/ControlCamera';
import { PlacementRate } from 'common/models/PlacementRate';

interface Props {
  placementId: number;
  loadingHandler?: (isLoading: boolean) => void;
  errorHandler?: (message: string) => void;
}

interface Props2 {
  data: TimelineUi;
  isSourceHovered: boolean;
  hideCallback: () => void;
}

interface TimelineUi extends PlacementRate {
  changeRequestTypeID: number;
  changeRequestType: string;
  consultantInfo1: string;
  consultantInfo2: string;
  consultantInfo3: string;
  consultantInfo4: string;
  consultantInfo5: string;
  margin: string;
  clientReference: string | null;
  range: number[];
  isEndDateNull: boolean;
}

const formatNumber = (value: number) => {
  return value.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 });
};

export default function FinancialsTimelineComponent({
  placementId,
  loadingHandler,
  errorHandler,
}: Props) {
  const [rows, setRows] = useState<TimelineUi[]>([]);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const t = useTheme();

  const current = useMemo(() => moment(), []);

  useEffect(() => {
    const getData = async () => {
      loadingHandler && loadingHandler(true);
      const res = (await GetPlacementFinancials(placementId, errorHandler)) as TimelineUi[] | null;
      if (res) {
        const data: TimelineUi[] = [];
        let minD = '';
        for (let i = 0; i < res.length; i++) {
          const row = res[i];
          if (row.startDate && row.startDate < minD) minD = row.startDate;
          const m1 = moment(row.startDate);
          const m2 =
            row.endDate && row.endDate !== '0001-01-01T00:00:00' ? moment(row.endDate) : current;
          let v1 = 0;
          let v2 = 0;
          let startDate = '';
          let endDate = '';

          if (m1.isValid()) {
            startDate = m1.format('DD MMM YYYY');
            v1 = m1.diff(current, 'days');
          }

          if (m2.isValid()) {
            endDate = m2.format('DD MMM YYYY');
            v2 = m2.diff(current, 'days');
          }

          data.push({
            ...row,
            startDate,
            endDate,
            range: [v1, v2],
            isEndDateNull: !row.endDate || row.endDate === '0001-01-01T00:00:00',
          });
        }

        setRows(data);
      }
      loadingHandler && loadingHandler(false);
    };
    getData();
  }, [current, errorHandler, loadingHandler, placementId]);

  const xAxisLabelsFormatter = useCallback((value: any) => {
    if (typeof value === 'number') {
      const m = moment();
      return m.add(value, 'days').format('DD MMM YYYY');
    } else if (typeof value === 'string') {
      const m = moment(value);
      if (m.isValid()) return m.format('MMM YYYY');
    }
    return '';
  }, []);

  const yAxisTickRenderer = useCallback(
    (tickProps: any) => {
      const { x, y, payload } = tickProps;
      if (tickProps && payload) {
        const index = payload.index;
        const r = rows[index];
        const crData = r.changeRequestID ? `CR ${r.changeRequestID}` : '';
        const rateType = r.placementRateType === 'Base Rate' ? 'Initial Rate' : r.placementRateType;
        const type = r.changeRequestType ? r.changeRequestType : rateType;
        const title = type.length > 40 ? type.substring(0, 37) + '...' : type;
        if (r) {
          return (
            <svg
              cursor="pointer"
              onMouseEnter={() => setHoverIndex(index)}
              onMouseLeave={() => setHoverIndex(null)}
              fontSize={t.typography.body2.fontSize}
              textLength={50}
            >
              <text x={x} y={y - 5} stroke={t.palette.text.primary}>
                <title>{type}</title>
                {title}
              </text>
              <text x={x} y={y + 15} stroke={t.palette.text.disabled}>
                <title>{crData}</title>
                {crData}
              </text>
            </svg>
          );
        }
      }
      return <></>;
    },
    [rows, t.palette.text.disabled, t.palette.text.primary, t.typography.body2.fontSize]
  );

  useEffect(() => {
    if (hoverIndex !== null) {
      const showPreviewTimeout = setTimeout(() => {
        setPreviewIndex(hoverIndex);
      }, 100);
      return () => clearTimeout(showPreviewTimeout);
    }
  }, [hoverIndex]);

  const selectedRow = useMemo(() => {
    if (previewIndex !== null) return rows[previewIndex];
    return null;
  }, [previewIndex, rows]);

  const hidePreviewCallback = useCallback(() => {
    setPreviewIndex(null);
  }, []);

  return (
    <>
      {selectedRow && (
        <CustomPreview
          isSourceHovered={hoverIndex !== null}
          data={selectedRow}
          hideCallback={hidePreviewCallback}
        />
      )}
      <Box height="calc(100% - 48px)">
        <ResponsiveContainer width="100%">
          <ComposedChart layout="vertical" data={rows} width={150} height={150}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="range"
              type="number"
              domain={['dataMin', 'dataMax']}
              tickFormatter={xAxisLabelsFormatter}
              stroke={t.palette.text.primary}
            />
            <YAxis
              dataKey="rateType"
              type="category"
              orientation="right"
              width={270}
              tick={yAxisTickRenderer}
            />
            <YAxis hide yAxisId="dots" orientation="right" />
            <Bar
              cursor="pointer"
              dataKey="range"
              fill={t.palette.primary.main}
              onMouseEnter={(data, index) => setHoverIndex(index)}
              onMouseLeave={() => setHoverIndex(null)}
            >
              {rows.map((entry, index) => (
                <Cell key={index} fill={t.palette.info.main} />
              ))}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </>
  );
}

const customWidth = 500;
const customHeight = 500;

const CustomPreview = ({ data, isSourceHovered, hideCallback }: Props2) => {
  const [isHovered, setIsHovered] = useState(false);
  const [shouldClose, setShouldClose] = useState(false);

  useEffect(() => {
    if (isHovered || isSourceHovered) setShouldClose(false);
    else setShouldClose(true);
  }, [isHovered, isSourceHovered]);

  useEffect(() => {
    if (shouldClose) {
      const hidePreviewTimeout = setTimeout(() => {
        hideCallback();
      }, 150);
      return () => clearTimeout(hidePreviewTimeout);
    }
  }, [shouldClose, hideCallback]);

  return (
    <Draggable
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
      defaultPosition={{
        x: window.innerWidth / 2 - customWidth / 2,
        y: window.innerHeight / 2 - customHeight / 2,
      }}
    >
      <Dialog
        open
        fullWidth
        PaperProps={{
          onMouseEnter: () => setIsHovered(true),
          onMouseLeave: () => setIsHovered(false),
          sx: { height: `${customHeight}px`, width: `${customWidth}px` },
        }}
        hideBackdrop
        sx={{ bottom: 'unset', right: 'unset', left: 'unset' }}
      >
        <DialogTitle component="div" display="flex" sx={PREVIEW_TITLE_STYLE}>
          <div style={{ alignSelf: 'center' }}>Financial Record</div>
          <div style={{ marginLeft: 'auto' }}>
            <IconButton
              id="draggable-dialog-title"
              sx={{ cursor: 'move', color: (t) => t.palette.primary.contrastText }}
            >
              <ControlCameraIcon />
            </IconButton>
            <IconButton
              onClick={() => hideCallback()}
              sx={{ color: (t) => t.palette.primary.contrastText }}
            >
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex' }}>
          <div
            style={{ paddingTop: '10px', display: 'flex', flexDirection: 'column', flexGrow: 1 }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <table style={PREVIEW_TABLE_STYLE}>
                  <tbody>
                    <tr>
                      <td width="30%" style={PREVIEW_TABLE_LABEL}>
                        Change Request
                      </td>
                      <td width="70%" style={PREVIEW_TABLE_CONTENT}>
                        {data.changeRequestID}
                      </td>
                    </tr>
                    <tr>
                      <td width="30%" style={PREVIEW_TABLE_LABEL}>
                        Start Date
                      </td>
                      <td width="70%" style={PREVIEW_TABLE_CONTENT}>
                        {data.startDate}
                      </td>
                    </tr>
                    <tr>
                      <td width="30%" style={PREVIEW_TABLE_LABEL}>
                        End Date
                      </td>
                      <td width="70%" style={PREVIEW_TABLE_CONTENT}>
                        {data.isEndDateNull ? '' : data.endDate}
                      </td>
                    </tr>
                    <tr>
                      <td width="30%" style={PREVIEW_TABLE_LABEL}>
                        Charge Rate
                      </td>
                      <td width="70%" style={PREVIEW_TABLE_CONTENT}>
                        {formatNumber(data.chargeRate) + ' ' + data.chargeRateUnitsType}
                      </td>
                    </tr>
                    <tr>
                      <td width="30%" style={PREVIEW_TABLE_LABEL}>
                        Pay Rate
                      </td>
                      <td width="70%" style={PREVIEW_TABLE_CONTENT}>
                        {formatNumber(data.payRate) + ' ' + data.payRateUnitsType}
                      </td>
                    </tr>
                    <tr>
                      <td width="30%" style={PREVIEW_TABLE_LABEL}>
                        On Costs
                      </td>
                      <td width="70%" style={PREVIEW_TABLE_CONTENT}>
                        {formatNumber(data.onCosts) + ' ' + data.onCostsUnitsType}
                      </td>
                    </tr>
                    <tr>
                      <td width="30%" style={PREVIEW_TABLE_LABEL}>
                        Margin
                      </td>
                      <td width="70%" style={PREVIEW_TABLE_CONTENT}>
                        {data.margin}
                      </td>
                    </tr>
                    <tr>
                      <td width="30%" style={PREVIEW_TABLE_LABEL}>
                        Client Ref
                      </td>
                      <td width="70%" style={PREVIEW_TABLE_CONTENT}>
                        {data.clientReference ?? ''}
                      </td>
                    </tr>
                    <tr>
                      <td width="30%" style={PREVIEW_TABLE_LABEL}>
                        Consultant 1
                      </td>
                      <td width="70%" style={PREVIEW_TABLE_CONTENT}>
                        {data.consultantInfo1 ?? ''}
                      </td>
                    </tr>
                    <tr>
                      <td width="30%" style={PREVIEW_TABLE_LABEL}>
                        Consultant 2
                      </td>
                      <td width="70%" style={PREVIEW_TABLE_CONTENT}>
                        {data.consultantInfo2 ?? ''}
                      </td>
                    </tr>
                    <tr>
                      <td width="30%" style={PREVIEW_TABLE_LABEL}>
                        Consultant 3
                      </td>
                      <td width="70%" style={PREVIEW_TABLE_CONTENT}>
                        {data.consultantInfo3 ?? ''}
                      </td>
                    </tr>
                    <tr>
                      <td width="30%" style={PREVIEW_TABLE_LABEL}>
                        Consultant 4
                      </td>
                      <td width="70%" style={PREVIEW_TABLE_CONTENT}>
                        {data.consultantInfo4 ?? ''}
                      </td>
                    </tr>
                    <tr>
                      <td width="30%" style={PREVIEW_TABLE_LABEL}>
                        Consultant 5
                      </td>
                      <td width="70%" style={PREVIEW_TABLE_CONTENT}>
                        {data.consultantInfo5 ?? ''}
                      </td>
                    </tr>
                    <tr>
                      <td width="30%" style={PREVIEW_TABLE_LABEL}>
                        Pay Comments
                      </td>
                      <td width="70%" style={PREVIEW_TABLE_CONTENT}>
                        {data.payComments ?? ''}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </Grid>
            </Grid>
          </div>
        </DialogContent>
      </Dialog>
    </Draggable>
  );
};
